import { createAsyncThunk } from "@reduxjs/toolkit";
import { openToast } from "../../modules/toast/store";
import { InsertUpdatedSecurityValues, SecuritiesUpdatedStagedState, SecurityProducer } from "./types";
import { FeedManagerState } from "../../modules/feedManager/store/types";
import { StatusBar } from "../../taskpane/components/statusBar/store/types";
import {
  moveSecurityFromStagingToSecuritiesValues,
  managePricesWithErrorSending,
  removeSecurityFromStagingArea,
} from ".";
import { setLastYieldSentAndTimestamp } from "../producerServices";
import { YieldType } from "../globalTypes";
import { resetStylesForDataSentSuccessfully } from "../producerServices/resetStylesForDataSentSuccessfully";
import { sendYieldValue } from "./utils";

interface SendSecurityPricesArgs {
  updatedRow: number;
  securityId: string;
  data?: InsertUpdatedSecurityValues;
  isResending?: boolean;
}

export const sendSecurityPrices = createAsyncThunk(
  "securitiesUpdatedStaged/sendSecurityPrices",
  async ({ securityId, updatedRow, data = null, isResending = false }: SendSecurityPricesArgs, thunkAPI) => {
    const appState = thunkAPI.getState() as {
      feedManager: FeedManagerState;
      statusBar: StatusBar;
      securitiesUpdatedStaged: SecuritiesUpdatedStagedState;
    };
    const { token: apiKey, environment } = appState.feedManager;
    const { isConnected } = appState.statusBar;

    if (!apiKey || !isConnected) {
      thunkAPI.dispatch(
        openToast({ title: "Error sending securities", type: "error", message: "You are not logged." })
      );
      throw new Error("No API key found.");
    }

    const dataToSend: SecurityProducer =
      data || appState.securitiesUpdatedStaged.stagedSecuritiesValuesUpdates[securityId];
    const { isin, quoteFeedId, bid, ask, quoteFeedName, securityName, isFallback } = dataToSend;

    const sendYieldValueObject = { isin, quoteFeedId, environment: environment.current, isFallback };
    const valuesToSend = [bid, ask].map((yieldValue) =>
      yieldValue
        ? sendYieldValue({
            ...sendYieldValueObject,
            yieldType: yieldValue === bid ? YieldType.BID : YieldType.ASK,
            yieldValue,
          })
        : null
    );

    const errorsInYields: { yieldType: YieldType; statusCode: number }[] = [];
    const sentYields: YieldType[] = [];

    if (quoteFeedId === "none") {
      ask && errorsInYields.push({ yieldType: YieldType.ASK, statusCode: 400 });
      bid && errorsInYields.push({ yieldType: YieldType.BID, statusCode: 400 });

      thunkAPI.dispatch(
        removeSecurityFromStagingArea({
          securityProducerId: securityId,
        })
      );

      return { updatedRow, errorsInYields };
    }

    await Promise.all(valuesToSend)
      .then((responses) => {
        responses.forEach((response) => {
          if (!response) return;

          if (response.status !== "ok")
            errorsInYields.push({ yieldType: response.yieldType, statusCode: response.statusCode });
          else {
            setLastYieldSentAndTimestamp({
              yieldType: response.yieldType,
              value: dataToSend[response.yieldType],
              row: updatedRow - 1,
            });

            sentYields.push(response.yieldType);

            resetStylesForDataSentSuccessfully({
              yieldToReset: response.yieldType,
              sendedRow: updatedRow,
            });
          }
        });
      })
      .then(() => {
        const actionPayload = {
          isin,
          quoteFeedId,
          bid,
          ask,
          quoteFeedName,
          securityName,
          securityProducerId: securityId,
          row: updatedRow,
        };

        if (!errorsInYields.length) {
          thunkAPI.dispatch(
            openToast({ title: "Success", type: "success", message: "The prices were sent successfully" })
          );

          if (!isResending) thunkAPI.dispatch(moveSecurityFromStagingToSecuritiesValues(actionPayload));
        } else {
          const yieldsWithErrors = errorsInYields.map((error) => error.yieldType).join(", ");
          thunkAPI.dispatch(
            openToast({
              title: "Error",
              type: "error",
              message: `The following yields cannot be sent successfully: ${yieldsWithErrors}`,
            })
          );

          thunkAPI.dispatch(
            managePricesWithErrorSending({
              updatedValues: actionPayload,
              yieldsWithErrors: errorsInYields,
              environment: environment.current,
            })
          );
        }
      });

    return { updatedRow, errorsInYields, securityProducerId: securityId, yieldsSentSuccessfully: sentYields };
  }
);
